// 12种基础背景色 - 用于生成用户头像颜色
const BASE_AVATAR_COLORS = [
  '#FF6B6B', // 红色
  '#4ECDC4', // 青色
  '#45B7D1', // 蓝色
  '#96CEB4', // 绿色
  '#FFEAA7', // 黄色
  '#DDA0DD', // 紫色
  '#98D8C8', // 薄荷绿
  '#F7DC6F', // 金黄色
  '#BB8FCE', // 淡紫色
  '#85C1E9', // 天蓝色
  '#F8C471', // 橙色
  '#82E0AA' // 浅绿色
];

// 明暗系数范围（用于生成不同深浅的头像主色）
const SHADE_RANGE = [0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9, 0.95];

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const isValidHex = (hex: string) => /^#[0-9A-Fa-f]{6}$/.test(hex);

const hexToRgb = (hex: string) => {
  if (!hex || !hex.startsWith('#') || hex.length !== 7) {
    return { r: 0, g: 0, b: 0 };
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

const rgbToHex = (r: number, g: number, b: number) =>
  `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;

const mixWithBlack = (hex: string, factor: number) => {
  if (!isValidHex(hex)) {
    return '#000000';
  }
  const { r, g, b } = hexToRgb(hex);
  const newR = Math.round(r * clamp(factor, 0, 1));
  const newG = Math.round(g * clamp(factor, 0, 1));
  const newB = Math.round(b * clamp(factor, 0, 1));
  return rgbToHex(newR, newG, newB);
};

const darkenColor = (hex: string, amount: number) => mixWithBlack(hex, 1 - clamp(amount, 0, 1));

const lightenColor = (hex: string, amount: number) => {
  if (!isValidHex(hex)) {
    return '#000000';
  }
  const { r, g, b } = hexToRgb(hex);
  const ratio = clamp(amount, 0, 1);
  const newR = Math.round(r + (255 - r) * ratio);
  const newG = Math.round(g + (255 - g) * ratio);
  const newB = Math.round(b + (255 - b) * ratio);
  return rgbToHex(newR, newG, newB);
};

const ensureContrast = (hex: string) => {
  if (!isValidHex(hex)) {
    return '#000000';
  }
  const { r, g, b } = hexToRgb(hex);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  if (luminance > 200) {
    return darkenColor(hex, 0.45);
  }

  if (luminance < 40) {
    return lightenColor(hex, 0.25);
  }

  return hex;
};

export const getUserAvatarColor = (userId: number) => {
  // 简单哈希函数，将userId打散
  const hash = (num: number) => {
    let h = num;
    h = ((h >> 16) ^ h) * 0x45d9f3b;
    h = ((h >> 16) ^ h) * 0x45d9f3b;
    h = (h >> 16) ^ h;
    return h >>> 0; // 确保是正数
  };

  const hashedId = hash(userId);

  // 使用哈希后的值选择颜色和深浅度
  const colorIndex = hashedId % BASE_AVATAR_COLORS.length;
  const shadeIndex = Math.floor(hashedId / BASE_AVATAR_COLORS.length) % SHADE_RANGE.length;

  const baseColor = BASE_AVATAR_COLORS[colorIndex];
  const shadeFactor = SHADE_RANGE[shadeIndex];
  const shadedColor = mixWithBlack(baseColor || '#000000', shadeFactor || 1);

  const avatarIconColor = ensureContrast(shadedColor);
  const avatarBackgroundColor = lightenColor(avatarIconColor, 0.8);
  return { avatarIconColor, avatarBackgroundColor };
};
